import { computed, Injectable, signal, Type } from '@angular/core';
import { NotificationComponent } from '@core/components/notification/notification/notification.component';
import { NotificationActionsComponent } from '@core/components/notification/notification-actions/notification-actions.component';
import { INotificationReference } from '@core/interfaces/notification-reference.interface';
import { ArrayOrder, sortProps } from '@core/utils/array.util';
import { APP_CONST } from '@core/constants/app.const';
import { NotificationVariant } from '@core/enums/notifications/notification-variant.enum';

type Options<TYPE extends NotificationComponent> = Omit<
  INotificationReference<TYPE>['inputs'],
  'text' | 'closeCallback'
> &
  Pick<INotificationReference<TYPE>, 'isPriority'>;

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private static readonly _notifications = signal<
    Map<string, INotificationReference<NotificationComponent>>
  >(new Map(), { equal: () => false });

  public static readonly notifications = computed(() =>
    sortProps(
      Array.from(NotificationService._notifications().values()),
      ['isPriority'],
      ArrayOrder.desc,
    ),
  );
  public static readonly notificationsIndex = NotificationService._notifications.asReadonly();

  public static show(content: string, options?: Options<NotificationComponent>): void {
    const { isPriority, ...inputs } = options || {};

    NotificationService.showOfType(NotificationComponent, {
      id: options?.id,
      isPriority,
      inputs: {
        text: content,
        ...inputs,
      },
    });
  }

  public static showError(
    content: string = APP_CONST.defaultErrorMessage,
    options?: Options<NotificationComponent>,
  ): void {
    NotificationService.show(content, {
      ...options,
      variant: NotificationVariant.ERROR,
      icon: options?.icon ?? { name: 'error' },
    });
  }

  public static showWithActions(
    content: string,
    options: Options<NotificationActionsComponent> &
      Required<Pick<Options<NotificationActionsComponent>, 'actions'>>,
  ): void {
    const { isPriority, ...inputs } = options || {};

    NotificationService.showOfType(NotificationActionsComponent, {
      id: options?.id,
      isPriority,
      inputs: {
        text: content,
        ...inputs,
      },
    });
  }

  public static hide(id: string): void {
    NotificationService._notifications.update((allNotifications) => {
      allNotifications.delete(id);
      return allNotifications;
    });
  }

  private static add<NOTIFICATION extends NotificationComponent>(
    notification: INotificationReference<NOTIFICATION>,
  ): void {
    NotificationService._notifications.update((allNotifications) => {
      allNotifications.set(notification.id, notification);
      return allNotifications;
    });
  }

  private static showOfType<NOTIFICATION extends NotificationComponent>(
    type: Type<NOTIFICATION>,
    ref: Omit<INotificationReference<NotificationComponent>, 'component'>,
  ): void {
    const id = ref.id ?? ref.inputs?.text ?? crypto.randomUUID();

    if (this.findAndResetDuplicated(id, ref.inputs?.duration ?? APP_CONST.notificationDuration)) {
      return;
    }

    const notificationRef: INotificationReference<NotificationComponent> = {
      id,
      component: type,
      isPriority: ref.isPriority ?? false,
      inputs: {
        ...ref.inputs,
        id,
        closeCallback: (id) => {
          NotificationService.hide(id);
        },
      },
    };

    NotificationService.add(notificationRef);
  }

  /**
   * @returns Whether or not there was a duplicated notification
   */
  private static findAndResetDuplicated(id: string, resetDuration: number): boolean {
    const existingNotification = this._notifications().get(id);

    if (!existingNotification) return false;
    if (existingNotification.inputs?.duration === null) return true;

    const savedDuration = existingNotification.inputs.duration;
    const newDuration = resetDuration + (savedDuration == resetDuration ? 1 : 0);

    (existingNotification.inputs as any).duration = newDuration;

    this._notifications.update((allNotifications) => allNotifications);
    return true;
  }
}
